import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  CheckIcon,
  ClipboardDocumentIcon,
  KeyIcon,
  NoSymbolIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { parseApiError } from '../../api/apiError';
import * as apiKeyService from '../../services/apiKeyService';
import { ApiKey, CreatedApiKey } from '../../services/apiKeyService';
import { getAllUsers, User } from '../../services/userService';
import Table from '../shared/Table';
import ReusableButton from '../shared/ReusableButton';
import ConfirmModal from '../shared/ConfirmModal';
import ApiDocs, { DEFAULT_BASE_URL } from './ApiDocs';

type KeyStatus = 'active' | 'inactive' | 'expired' | 'revoked';

const STATUS_STYLES: Record<KeyStatus, string> = {
  active: 'bg-green-50 text-green-700 ring-green-600/20',
  inactive: 'bg-slate-50 text-slate-600 ring-slate-500/20',
  expired: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  revoked: 'bg-red-50 text-red-700 ring-red-600/20',
};

const inputClassName =
  'block w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400';

function keyStatus(apiKey: ApiKey): KeyStatus {
  if (apiKey.revokedAt) return 'revoked';
  if (apiKey.expiresAt && new Date(apiKey.expiresAt) <= new Date()) return 'expired';
  // the backend leaves the domain empty once the owner lost the domain admin
  // role or their last domain, and then rejects the key
  if (!apiKey.domain) return 'inactive';
  return 'active';
}

function formatDate(value: string | null, withTime = false): string {
  if (!value) return '–';
  return format(new Date(value), withTime ? 'MMM d, yyyy HH:mm' : 'MMM d, yyyy');
}

// Same rule as the backend: a key submits to its owner's domain with the lowest id
function primaryDomain(user: User): string | null {
  const domains = [...(user.assignedDomains ?? [])].sort((a, b) => a.id - b.id);
  return domains[0]?.hostname ?? null;
}

const ApiKeys: React.FC = () => {
  const { t } = useTranslation();
  const { role, assignedDomains, loading: authLoading } = useAuth();
  const isAdmin = role === 'admin';

  // Domain admins see their own domain, which is where their keys submit to:
  // the first of assignedDomains, which the backend sorts by id
  const docsBaseUrl =
    role === 'domain_admin' && assignedDomains[0] ? `https://${assignedDomains[0]}/api` : DEFAULT_BASE_URL;

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [creating, setCreating] = useState(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [domainAdmins, setDomainAdmins] = useState<User[]>([]);
  const [domainAdminsState, setDomainAdminsState] = useState<'loading' | 'loaded' | 'failed'>('loading');

  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);

  const loadKeys = useCallback(() => {
    setLoading(true);
    apiKeyService
      .getApiKeys()
      .then((data) => {
        setKeys(data);
        setError('');
      })
      .catch(() => setError(t('apiKeys.errorLoading')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  // Admins issue keys on behalf of a domain admin, so they need the candidates
  useEffect(() => {
    if (!isAdmin) return;
    getAllUsers()
      .then((users) => {
        setDomainAdmins(users.filter((user) => user.role === 'domain_admin' && primaryDomain(user)));
        setDomainAdminsState('loaded');
      })
      .catch(() => setDomainAdminsState('failed'));
  }, [isAdmin]);

  const minExpiryDate = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return format(tomorrow, 'yyyy-MM-dd');
  }, []);

  const resetForm = () => {
    setShowCreate(false);
    setName('');
    setOwnerId('');
    setExpiresOn('');
    setFormErrors([]);
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreating(true);
    setFormErrors([]);

    try {
      const created = await apiKeyService.createApiKey({
        name: name.trim(),
        userId: isAdmin ? Number(ownerId) : undefined,
        // valid through the end of the selected day
        expiresAt: expiresOn ? new Date(`${expiresOn}T23:59:59`).toISOString() : undefined,
      });
      resetForm();
      setCreatedKey(created);
      setCopyState('idle');
      loadKeys();
    } catch (err) {
      const { messages } = parseApiError(err, {
        generic: t('apiKeys.errorCreating'),
        network: t('common.networkError'),
      });
      setFormErrors(messages);
    } finally {
      setCreating(false);
    }
  };

  // The clipboard API is missing outside secure contexts and may be denied;
  // the key stays visible for copying by hand
  const handleCopyKey = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await apiKeyService.revokeApiKey(revokeTarget.id);
      loadKeys();
    } catch {
      setError(t('apiKeys.errorRevoking'));
    } finally {
      setRevokeTarget(null);
    }
  };

  const headers = [
    t('apiKeys.name'),
    t('apiKeys.key'),
    ...(isAdmin ? [t('apiKeys.owner')] : []),
    t('common.domain'),
    t('apiKeys.created'),
    t('apiKeys.lastUsed'),
    t('apiKeys.expires'),
    t('apiKeys.status'),
    t('common.actions'),
  ];

  const renderRow = (apiKey: ApiKey) => {
    const status = keyStatus(apiKey);
    return (
      <>
        <td className="border px-2 py-2 break-words">{apiKey.name}</td>
        <td className="border px-2 py-2 font-mono text-xs break-all">{apiKey.keyPrefix}…</td>
        {isAdmin && (
          <td className="border px-2 py-2 break-words">
            {apiKey.user.name}
            <br />
            <span className="text-slate-500 break-all">{apiKey.user.email}</span>
          </td>
        )}
        <td className="border px-2 py-2 break-all">{apiKey.domain ?? '–'}</td>
        <td className="border px-2 py-2 whitespace-nowrap">{formatDate(apiKey.createdAt)}</td>
        <td className="border px-2 py-2">{apiKey.lastUsedAt ? formatDate(apiKey.lastUsedAt, true) : t('apiKeys.never')}</td>
        <td className="border px-2 py-2 whitespace-nowrap">{formatDate(apiKey.expiresAt)}</td>
        <td className="border px-2 py-2">
          <span
            title={status === 'inactive' ? t('apiKeys.inactiveHint') : undefined}
            className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${STATUS_STYLES[status]}`}
          >
            {t(`apiKeys.${status}`)}
          </span>
        </td>
        <td className="border px-2 py-2">
          {status !== 'revoked' && (
            <ReusableButton
              onClick={() => setRevokeTarget(apiKey)}
              icon={<NoSymbolIcon className="size-5" aria-hidden="true" />}
              label={t('apiKeys.revoke')}
              className="my-1 bg-red-600 hover:bg-red-500"
            />
          )}
        </td>
      </>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('apiKeys.title')}</h1>
          <p className="text-slate-600 mt-1">{t('apiKeys.subtitle')}</p>
        </div>
        {/* waits for the role: admins must pick the key's owner */}
        {!showCreate && !authLoading && (
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-sm hover:shadow-md transition-all"
          >
            <PlusIcon className="size-5" />
            <span>{t('apiKeys.createKey')}</span>
          </button>
        )}
      </div>

      {/* One-time reveal of a freshly created key */}
      {createdKey && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-green-900">{t('apiKeys.newKeyTitle')}</h2>
              <p className="text-sm text-green-800 mt-1">{t('apiKeys.newKeyHint')}</p>
            </div>
            <button onClick={() => setCreatedKey(null)} className="text-green-700 hover:text-green-900" aria-label={t('apiKeys.done')}>
              <XMarkIcon className="size-5" />
            </button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <code className="flex-1 rounded-lg border border-green-300 bg-white px-3 py-2.5 font-mono text-sm text-slate-900 break-all">
              {createdKey.key}
            </code>
            <button
              onClick={handleCopyKey}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg"
            >
              {copyState === 'copied' ? <CheckIcon className="size-5" /> : <ClipboardDocumentIcon className="size-5" />}
              {copyState === 'copied' ? t('apiKeys.copied') : t('apiKeys.copy')}
            </button>
          </div>
          {copyState === 'failed' && <p className="text-sm text-red-700">{t('apiKeys.copyFailed')}</p>}
          {createdKey.domain && (
            <p className="text-sm text-green-800">{t('apiKeys.newKeyDomain', { domain: createdKey.domain })}</p>
          )}
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">{t('apiKeys.createKey')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">{t('apiKeys.name')} *</span>
              <input
                type="text"
                required
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('apiKeys.namePlaceholder')}
                className={`${inputClassName} mt-1`}
              />
            </label>

            {isAdmin && (
              <label className="block">
                <span className="text-sm font-medium text-slate-700">{t('apiKeys.owner')} *</span>
                <select
                  required
                  value={ownerId}
                  onChange={(e) => setOwnerId(e.target.value)}
                  className={`${inputClassName} mt-1`}
                >
                  <option value="">{t('apiKeys.selectOwner')}</option>
                  {domainAdmins.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email}) – {primaryDomain(user)}
                    </option>
                  ))}
                </select>
                {domainAdminsState === 'failed' && (
                  <span className="mt-1 block text-sm text-red-700">{t('apiKeys.errorLoadingDomainAdmins')}</span>
                )}
                {domainAdminsState === 'loaded' && domainAdmins.length === 0 && (
                  <span className="mt-1 block text-sm text-slate-500">{t('apiKeys.noDomainAdmins')}</span>
                )}
              </label>
            )}

            <label className="block">
              <span className="text-sm font-medium text-slate-700">{t('apiKeys.expiresOn')}</span>
              <input
                type="date"
                min={minExpiryDate}
                value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)}
                className={`${inputClassName} mt-1`}
              />
            </label>
          </div>

          {formErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              {formErrors.map((message) => (
                <p key={message} className="text-sm text-red-700">
                  {message}
                </p>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-xl"
            >
              <KeyIcon className="size-5" />
              {t('apiKeys.create')}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium rounded-xl"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      {/* Keys */}
      {(loading || authLoading) && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
            <p className="mt-2 text-slate-600">{t('apiKeys.loading')}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {!loading && !authLoading && !error && keys.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <KeyIcon className="mx-auto h-12 w-12 text-slate-400" />
          <h3 className="mt-4 text-lg font-semibold text-slate-900">{t('apiKeys.noKeys')}</h3>
          <p className="mt-2 text-slate-600">{t('apiKeys.noKeysDescription')}</p>
        </div>
      )}

      {!loading && !authLoading && keys.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto px-4">
            <Table headers={headers} data={keys} renderRow={renderRow} keyExtractor={(apiKey) => apiKey.id} />
          </div>
        </div>
      )}

      {/* Documentation */}
      <div className="pt-4 space-y-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{t('apiKeys.docsTitle')}</h2>
          <p className="text-slate-600 mt-1">{t('apiKeys.docsSubtitle')}</p>
        </div>
        <ApiDocs baseUrl={docsBaseUrl} />
      </div>

      {revokeTarget && (
        <ConfirmModal
          isOpen
          onClose={() => setRevokeTarget(null)}
          onConfirm={handleRevoke}
          title={t('apiKeys.revokeTitle')}
          message={t('apiKeys.revokeMessage', { name: revokeTarget.name })}
          confirmLabel={t('apiKeys.revoke')}
        />
      )}
    </div>
  );
};

export default ApiKeys;
